(ns app.front.view.bond.common
  (:require [app.domain.attachment.model :as attachment-model]
            [app.domain.bond.model :as bond-model]
            [app.domain.util :as du]
            [app.front.entities :as entities]
            [app.front.ui :as ui]
            [app.front.ui.action-buttons :as action-buttons]
            [app.front.ui.content-switcher :as ui-cs]
            [app.front.ui.modal :as modal]
            [app.front.ui.money-input :as mi]
            [app.front.ui.rich-text :as ui-rt]
            [app.front.ui.section :as section]
            [app.front.ui.select :as ui-select]
            [app.front.ui.separator :as separator]
            [app.front.ui.sortable-table :as sortable-table]
            [app.front.ui.table :as ui-table]
            [app.front.ui.util :as uiu]
            [app.front.util :as futil :refer [for-frag]]
            [app.front.validate :as validate]
            [app.front.view.attachment.list :as attachment-list]
            [app.front.view.bond.subs :as bond-subs]
            [app.front.view.bond.util :as bond-util]
            [app.front.view.bond.view :as-alias bond-view]
            [app.front.view.workspace.util :as ws-util]
            [app.rich-text :as rich-text]
            [clojure.string :as str]
            [core.front.carbon :as cc]
            [core.front.config :as front-config]
            [core.front.date-util :as date-util]
            [core.front.event :refer [<sub >evt]]
            [core.user.model :as user-model]
            [core.util.util :as util]
            [re-frame.core-instrumented :as rf]
            [re-frame.std-interceptors :as interceptors]
            [reagent.core :as r]))

(def ^:private state-path (interceptors/path ::state))

(rf/reg-sub ::state :-> ::state)

(rf/reg-sub ::bond-required-attachment-types
  (fn [[_ bond-id]]
    (rf/subscribe [::entities/entity {:db/id bond-id}]))
  (fn [bond _]
    (some-> bond :bond/template :bond-template/required-attachment-types)))

(rf/reg-sub ::bond-attachments
  (fn [[_ bond-id]]
    (rf/subscribe [::entities/entities
                   {:entity/parent bond-id
                    :entity/type   :entity.type/attachment}]))
  :-> identity)

(rf/reg-sub ::bond-attachment-requirements
  (fn [[_ bond-id]]
    [(rf/subscribe [::bond-required-attachment-types bond-id])
     (rf/subscribe [::bond-attachments bond-id])
     (rf/subscribe [:language])])
  (fn [[required-attachments added-attachments lang] _]
    (let [type-counts (frequencies (mapcat :attachment/types added-attachments))]
      (some->> (seq required-attachments)
               (map (fn [att-type]
                      (let [attachment-count (get type-counts att-type 0)
                            border-color     (if (pos? attachment-count)
                                               "border-ui-green"
                                               "border-ui-red-100")
                            title            (attachment-model/attachment-type-label att-type lang)]
                        {:db/id                        (:db/id att-type)
                         :requirement/attachment-type  att-type
                         :requirement/attachment-count attachment-count
                         :requirement/border-color     border-color
                         :requirement/title            title})))
               (sort-by :requirement/title)))))

(rf/reg-sub ::bond-has-all-required-attachments?
  (fn [[_ bond-id]]
    [(rf/subscribe [::bond-required-attachment-types bond-id])
     (rf/subscribe [::bond-attachments bond-id])])
  (fn [[required-types attachments] _]
    (let [provided-types (into #{} (mapcat :attachment/types) attachments)]
      (every? #(contains? provided-types %) required-types))))

(rf/reg-sub ::bond-tab
  :<- [::state]
  :-> :bond-tab)

(rf/reg-event-fx ::get-bond-attachments
  (fn [_ [_ bond-id]]
    {:comm/command {:command    :bond/get-attachments
                    :method     :get
                    :data       {:bond-id bond-id}
                    :on-success [::entities/merge-entity-db]}}))

(rf/reg-event-fx ::load-bond-attachments-tab
  (fn [_ [_ bond-id]]
    {:fx [[:dispatch [::get-bond-attachments bond-id]]]}))

(rf/reg-event-db ::sync-workspace-attachment-types
  (fn [db _]
    (let [bond-id (get-in db [::bond-view/state :bond-id])
          bond    (entities/find-entity db {:bond/id bond-id})
          att-ids (some->> bond :bond/group :group/available-bond-attachments
                           (map :db/id) set)]
      (assoc-in db [:view/workspace :attachment-types] (or att-ids #{})))))

(rf/reg-event-fx ::bond-tab-changed
  [state-path]
  (fn [{state :db} [_ selected-tab bond-id]]
    {:db (assoc state :bond-tab selected-tab)
     :fx [(case selected-tab
            :bond/audit-log
            [:dispatch [::fetch-bond-audit-events bond-id]]
            :bond/attachments
            [:dispatch [::load-bond-attachments-tab bond-id]]
            nil)]}))

(rf/reg-event-fx ::fetch-bond-audit-events
  (fn [_ [_ bond-id]]
    {:comm/command {:command    :bond/get-bond-audit-events
                    :method     :get
                    :data       {:bond-id bond-id}
                    :on-success [::entities/merge-entity-db]}}))

(rf/reg-sub ::bond-audit-events
  (fn [[_ bond-id]]
    (rf/subscribe [::entities/entities {:entity/type   :entity.type/bond-audit-event
                                        :entity/parent bond-id}]))
  (fn [events _]
    (sort-by :entity/created > events)))

(defn format-bond-amount
  [{:keys [bond/group bond/amount]}]
  (let [currency (:group/currency group)]
    (if (and currency amount)
      (futil/money-string amount currency)
      "-")))

(defn format-bond-expiration
  [lang {:keys [bond/expires-on]}]
  (if expires-on
    (date-util/date lang expires-on)
    "-"))

(defn format-owner-email
  "Format the owner email. For pending-invite bonds an email is fetched from tasks,
   unless the invited party is a business (in which case leave the email empty)."
  [owner-or-bond]
  (or (:user/email owner-or-bond)
      (:business/email owner-or-bond)
      (when-not (bond-model/claim-task-user-group owner-or-bond)
        (bond-model/claim-task-email owner-or-bond))
      "-"))

(defn bond-status-colors [{state :domain/state}]
  (case state
    :bond.state/unverified       :attention
    :bond.state/verified         :ok
    :bond.state/expired          :warning
    :bond.state/closed           :notification
    :bond.state/pending-invite   :task
    :bond.state/cancelled-invite :notification
    nil))

(rf/reg-event-fx ::fetch-related-workspaces.success
  [state-path]
  (fn [{state :db} [_ status {:keys [entities active-workspaces past-workspaces]}]]
    {:fx [[:dispatch [::entities/merge-entity-db status entities]]]
     :db (-> state
             (update :active-workspaces merge active-workspaces)
             (update :past-workspaces merge past-workspaces)
             (assoc :related-workspaces-loading? false))}))

(rf/reg-sub ::related-workspaces-loading?
  :<- [::state]
  :-> :related-workspaces-loading?)

(rf/reg-event-fx ::fetch-related-workspaces
  [state-path]
  (fn [{state :db} [_ bond-id]]
    {:comm/command {:command    :bond/get-related-workspaces
                    :method     :get
                    :on-success [::fetch-related-workspaces.success]
                    :data       {:bond-id bond-id}}
     :db           (assoc state :related-workspaces-loading? true)}))

(rf/reg-sub ::past-workspaces
  :<- [::state]
  (fn [state [_ bond-id]]
    (get-in state [:past-workspaces bond-id])))

(rf/reg-sub ::active-workspaces
  :<- [::state]
  (fn [state [_ bond-id]]
    (get-in state [:active-workspaces bond-id])))

(defn applicant-selector
  [{:keys [tr value options show-errors? validator val->str on-change disabled?]}]
  [:div.flex-1.min-w-0
   [ui-select/select
    {:id           "bond-applicant"
     :label-text   (tr [:bond/applicant])
     :val->str     (or val->str :name)
     :select-text  (tr [:select-option])
     :class-name   "w-full"
     :value        value
     :required     true
     :disabled     disabled?
     :invalid      (validate/field-invalid? show-errors? validator :bond/owner)
     :invalid-text (validate/field-invalid-text show-errors? validator :bond/owner tr)
     :on-change    on-change
     :values       options}]])

(defn template-selector
  [{:keys [tr value options show-errors? validator val->str on-change]}]
  [:div.flex-1.min-w-0
   [ui-select/select
    {:id           "bond-template"
     :class-name   "w-full"
     :val->str     (or val->str :name)
     :label-text   (tr [:bond/choose-template])
     :select-text  (tr [:select-option])
     :value        value
     :required     true
     :invalid      (validate/field-invalid? show-errors? validator :bond/template)
     :invalid-text (validate/field-invalid-text show-errors? validator :bond/template tr)
     :values       options
     :on-change    on-change}]])

(defn external-id-input
  [{:keys [tr value show-errors? validator on-change conflict? required]}]
  (let [validation-invalid? (validate/field-invalid? show-errors? validator :bond/external-id)
        invalid?            (or validation-invalid? conflict?)
        invalid-text        (cond
                              conflict? (tr [:bond.new/account-number-already-exists])
                              validation-invalid? (validate/field-invalid-text show-errors? validator :bond/external-id tr))]
    [:div.flex-1.min-w-0
     [ui/text-input
      {:id                 "bond-account-number"
       :wrapper-class-name "w-full"
       :hide-unit?         true
       :value              value
       :required           required
       :invalid            invalid?
       :invalid-text       invalid-text
       :on-change          on-change
       :label-text         (tr [:bond/account-number])}]]))

(defn amount-input
  [{:keys [tr value show-errors? validator on-change required?]
    :or   {required? true}}]
  [:div.flex-1.min-w-0
   [mi/money-input
    {:id            "bond-amount"
     :label-text    (tr [:bond/amount])
     :default-value value
     :required      required?
     :invalid       (validate/field-invalid? show-errors? validator :bond/amount)
     :invalid-text  (validate/field-invalid-text show-errors? validator :bond/amount tr)}
    on-change]])

(defn expiration-date-picker
  [{:keys [id tr value show-errors? validator on-change allow-date? required?]
    :or   {required? true}}]
  (let [allow-date? (or allow-date? #(date-util/after? % (js/Date.)))]
    [:div.flex-1.min-w-0
     [ui/date-picker
      {:id          (or id "bond-expiry")
       :width       "100%"
       :class-name  "w-full"
       :fixed?      false
       :required?   required?
       :invalid?    (validate/field-invalid? show-errors? validator :bond/expires-on)
       :callback    on-change
       :value       value
       :label-text  (tr [:bond/expires-on])
       :allow-date? allow-date?}]]))

(defn company-name-input
  [{:keys [tr value show-errors? validator on-change required?]
    :or   {required? true}}]
  [:div.flex-1.min-w-0
   [ui/text-input
    {:id           "bond-company-name"
     :width        "w-full"
     :class-name   "w-full"
     :value        value
     :required     required?
     :invalid      (validate/field-invalid? show-errors? validator :bond/issuer-name)
     :invalid-text (validate/field-invalid-text show-errors? validator :bond/issuer-name tr)
     :on-change    on-change
     :label-text   (tr [:bond/issuer-name])}]])

(defn company-phone-input
  [{:keys [tr value show-errors? validator on-change required? country]}]
  [:div.flex-1.min-w-0
   [ui/intl-tel-input
    {:id              "bond-company-phone"
     :label-text      (tr [:bond/issuer-phone])
     :value           value
     :required        required?
     :invalid         (validate/field-invalid? show-errors? validator :bond/issuer-phone)
     :invalid-text    (validate/field-invalid-text show-errors? validator :bond/issuer-phone tr)
     :callback        on-change
     :initial-country country}]])

(defn company-email-input
  [{:keys [tr value show-errors? validator on-change required?]}]
  [:div.flex-1.min-w-0
   [ui/text-input
    {:id           "bond-company-email"
     :width        "w-full"
     :class-name   "w-full"
     :type         "email"
     :value        value
     :required     required?
     :invalid      (validate/field-invalid? show-errors? validator :bond/issuer-email)
     :invalid-text (validate/field-invalid-text show-errors? validator :bond/issuer-email tr)
     :on-change    on-change
     :label-text   (tr [:bond/issuer-email])}]])

(defn description-input
  [{:keys [tr value show-errors? validator on-change required?]
    :or   {required? true}}]
  [:div.flex.flex-row.gap-y-3.mt-2
   [ui/text-area
    {:id           "bond-description"
     :label-text   (tr [:bond/description])
     :value        value
     :on-change    on-change
     :invalid      (validate/field-invalid? show-errors? validator :bond/description)
     :invalid-text (validate/field-invalid-text show-errors? validator :bond/description tr)
     :required     required?}]])

(def bond-form-row
  (uiu/wrap-in :div.bx--row :div.bx--col-xs-12.bx--col-md-3.mb-6))

(defn bond-attachment-requirement
  [{:requirement/keys [attachment-count border-color title]}]
  [:div.attachment-requirement.bg-ui-light-gray.p-3.border-solid.border-l-4.flex.justify-between.items-center.leading-tight.relative.overflow-hidden
   {:class border-color}
   [:div.mr-2.font-bold.w-full title]
   [:div.px-3.py-1.font-bold.bg-ui-night-sky-100.text-ui-white attachment-count]])

(defn bond-attachment-requirements-list [tr bond-id req-attachments?]
  (let [requirements (<sub [::bond-attachment-requirements bond-id])
        can-verify?  (<sub [::bond-subs/can-verify-bond? bond-id])]
    (when (seq requirements)
      [:div.relative
       [:div.mb-3>strong (tr [:required-attachments])]
       (when-not req-attachments?
         [:p.mt-2.text-sm.text-ui-attention-100.italic
          (tr  [(if can-verify?
                  :bond/verify-disabled-missing-attachments
                  :bond/verify-disabled-communication-with-applicant)])])
       [:div.grid.sm:grid-cols-2.lg:grid-cols-3.xl:grid-cols-4.gap-3
        (for-frag [requirement requirements]
          [bond-attachment-requirement requirement])]])))

(defn bond-details [bond tr]
  (let [lang (<sub [:language])]
    [:div.flex.flex-row.flex-wrap.-mx-2.mt-2.gap-y-3
     (for-frag [[k v] {:bond/group-name   (-> bond :bond/group :group/name)
                       :bond/amount       (format-bond-amount bond)
                       :bond/type         (bond-model/bond-type-name bond lang)
                       :bond/expires-on   (format-bond-expiration lang bond)
                       :bond/issuer-name  (:bond/issuer-name bond)
                       :bond/issuer-phone (:bond/issuer-phone bond)
                       :bond/issuer-email (:bond/issuer-email bond)}]
       [:div.w-full.sm:w-1-2.lg:w-1-4.px-2.my-2
        [:<>
         [:div.bx--label (tr [k])]
         [:div.label-value v]]])]))

(defn- render-rich-text
  "Render sanitized HTML rich text."
  [html-str]
  (when html-str
    [ui-rt/editor-legacy {:initial-value html-str}]))

(defn- format-audit-value
  "Format audit event value based on attribute type.
   For :bond/expires-on, displays date in group timezone.
   Returns HTML fragment for date values."
  [changed-attribute value currency tr group-tz]
  (case changed-attribute
    :bond/expires-on (if (and value group-tz)
                       (str (date-util/date-in-tz value group-tz)
                            " "
                            (date-util/time-zone-info group-tz "short" true))
                       "-")
    :bond/amount     (futil/money-string value currency)
    :domain/state    (tr [value])
    :bond/template   value
    value))

(def ^:private created-event-display-fields
  "Fields to display for bond creation audit events."
  (set (into bond-model/bond-application-required-keys bond-model/bond-application-optional-keys)))

(defn audit-log-columns [tr format-date-time currency group-tz]
  [[(tr [:date/date]) #(format-date-time (:entity/created %))]
   [(tr [:user/user]) #(or (user-model/full-name (:entity/creator %)) (tr [:system]))]
   [(tr [:bond/audit-log-details])
    (fn [{:bond-audit-event/keys [type changed-attribute new-attribute-value] :as e}]
      (case type
        :bond.audit/attribute-change
        [:span (tr [changed-attribute]) ": " (format-audit-value changed-attribute
                                                                 new-attribute-value
                                                                 currency
                                                                 tr
                                                                 group-tz)]

        (:bond.audit/modified :bond.audit/created)
        (reduce (fn [acc [attr value]]
                  (conj acc [:span (tr [attr]) ": " (format-audit-value attr value currency tr group-tz)]))
                [:div.flex.flex-col.gap-2 [:span.mb-4 (tr [type])]]
                (select-keys new-attribute-value created-event-display-fields))

        (:bond.audit/associated :bond.audit/disassociated)
        [:div.flex.flex-col.gap-2
         [:span.mb-4 (tr [type])]
         [:a {:href (:related-workspace-url e)} (:workspace/id e)]]

        (:bond.audit/attachment-added :bond.audit/attachment-removed)
        [:div.flex.flex-col.gap-2
         [:span.mb-4 (tr [type])]
         [:span new-attribute-value]]

        :bond.audit/verified
        [:span (tr [type])]

        :bond.audit/closed
        [:span (tr [type])]

        :bond.audit/cancelled-invite
        [:span (tr [type])]

        :bond.audit/reopened
        [:span (tr [type])]

        [:span (str type)]))]
   [(tr [:bond/reason]) #(render-rich-text (:bond-audit-event/change-reason %))]])

(defn bond-audit-log [bond-id tr currency group-tz]
  (let [events           (<sub [::bond-audit-events bond-id])
        format-date-time (<sub [::date-util/date-time-fn])
        loading?         (<sub [:comm/command-processing? :bond/get-bond-audit-events])]
    (cond
      loading?        [section/section-skeleton]
      (empty? events) [:div.mt-4 (tr [:bond/audit-log-no-events])]
      :else
      [:div.mt-4
       [ui-table/modern-responsive-table
        {:items events
         :tr    tr
         :cols  (audit-log-columns tr format-date-time currency group-tz)}]])))

(defn workspace-table [workspaces tr]
  [sortable-table/table
   {:id            "related-workspaces-table"
    :cols          [[(tr [:workspace/id]) :workspace/id {:sorting true}]
                    [(tr [:workspace/address]) :workspace/address {:sorting true}]
                    [(tr [:domain/state]) #(tr [(:workspace/target-state %)]) {:sorting true}]]
    :items         workspaces
    :configurable? true
    :tr            tr
    :link          {:description (fn [workspace]
                                   (if (ws-util/resume-application? workspace)
                                     (tr [:search/resume-application])
                                     (tr [:open])))
                    :make-href   ws-util/workspace-or-resume-application-href}}])

(defn active-workspaces-view [bond tr]
  (let [active-workspace-ids (<sub [::active-workspaces (:db/id bond)])
        active-workspaces    (entities/find-entities (<sub [::entities/context])
                                                     (set active-workspace-ids))]
    (when-not (<sub [::related-workspaces-loading?])
      (if (empty? active-workspace-ids)
        [:p (tr [:bond/no-active-workspaces])]
        (workspace-table active-workspaces tr)))))

(defn past-workspaces-view [bond tr]
  (let [past-workspace-ids (<sub [::past-workspaces (:db/id bond)])
        past-workspaces    (entities/find-entities (<sub [::entities/context])
                                                   (set past-workspace-ids))]
    (when-not (<sub [::related-workspaces-loading?])
      (if (empty? past-workspace-ids)
        [:p (tr [:bond/no-past-workspaces])]
        (workspace-table past-workspaces tr)))))

(defn match-option
  [options selected]
  (let [selected-id (or (:id selected) (:db/id selected) selected)]
    (or (some #(when (= (:id %) selected-id) %) options)
        selected)))

(defn bond-edit-form-fields
  "Pure component for rendering bond edit form fields.
   Takes pre-resolved values and callbacks as a props map.

   Required props:
     :tr              - translation function
     :bond-id         - bond identifier (for unique element IDs)
     :edit            - edit state map with :bond/* keys
     :show-errors?    - whether to show validation errors
     :validator       - validation fn
     :required-fields - set of required field keywords
     :country         - country code for phone input
     :applicant-opts  - options for applicant selector [{:id :name}]
     :template-opts   - options for template selector [{:id :name}]
     :on-field-change - (fn [field-key value]) callback for field changes
     :pending-invite? - whether the bond is in pending-invite state"
  [{:keys [tr bond-id edit show-errors? validator required-fields country
           applicant-opts template-opts on-field-change pending-invite?]}]
  [:<>
   [bond-form-row
    [applicant-selector
     {:tr           tr
      :value        (match-option applicant-opts (:bond/owner edit))
      :options      applicant-opts
      :show-errors? show-errors?
      :validator    validator
      :val->str     #(or (:name %) (tr [:loading-options]))
      :disabled?    true
      :on-change    #(on-field-change :bond/owner %)}]
    [template-selector
     {:tr           tr
      :value        (match-option template-opts (:bond/template edit))
      :options      template-opts
      :show-errors? show-errors?
      :validator    validator
      :val->str     #(or (:name %) (tr [:loading-options]))
      :on-change    #(on-field-change :bond/template %)}]
    [external-id-input
     {:tr           tr
      :value        (:bond/external-id edit)
      :show-errors? show-errors?
      :validator    validator
      :required     (not pending-invite?)
      :on-change    #(on-field-change :bond/external-id (futil/tval %))}]
    [amount-input
     {:tr           tr
      :value        (:bond/amount edit)
      :show-errors? show-errors?
      :validator    validator
      :required?    (not pending-invite?)
      :on-change    #(on-field-change :bond/amount %)}]]
   [bond-form-row
    [expiration-date-picker
     {:id           (when bond-id (str "bond-expiry-" bond-id))
      :tr           tr
      :value        (:bond/expires-on edit)
      :show-errors? show-errors?
      :validator    validator
      :required?    (not pending-invite?)
      :allow-date?  #(date-util/after? % (js/Date.))
      :on-change    #(on-field-change :bond/expires-on %)}]
    [company-name-input
     {:tr           tr
      :value        (:bond/issuer-name edit)
      :show-errors? show-errors?
      :validator    validator
      :required?    (not pending-invite?)
      :on-change    #(on-field-change :bond/issuer-name (futil/tval %))}]
    [company-phone-input
     {:tr           tr
      :value        (:bond/issuer-phone edit)
      :show-errors? show-errors?
      :validator    validator
      :required?    (and (not pending-invite?) (contains? required-fields :bond/issuer-phone))
      :country      country
      :on-change    #(on-field-change :bond/issuer-phone %)}]
    [company-email-input
     {:tr           tr
      :value        (:bond/issuer-email edit)
      :show-errors? show-errors?
      :validator    validator
      :required?    (and (not pending-invite?) (contains? required-fields :bond/issuer-email))
      :on-change    #(on-field-change :bond/issuer-email (futil/tval %))}]]
   [description-input
    {:tr           tr
     :value        (:bond/description edit)
     :show-errors? show-errors?
     :validator    validator
     :required?    (not pending-invite?)
     :on-change    #(on-field-change :bond/description (futil/tval %))}]])

(defn full-edit-form
  "Inline edit form for unverified bonds."
  [bond tr & {:keys [wrapper]}]
  (let [entity-id       (:db/id bond)
        bond-id         (:bond/id bond)
        pending-invite? (du/in-state? :bond.state/pending-invite bond)
        dirty?          (<sub [::bond-subs/full-edit-dirty? entity-id])
        edit            (<sub [::bond-subs/edit])
        saving?         (<sub [::bond-subs/edit-saving?])
        show-errors?    (<sub [::bond-subs/edit-show-errors?])
        validator       (<sub [::bond-subs/edit-validator entity-id])
        required-fields (<sub [::bond-subs/edit-group-required-bond-fields entity-id])
        country         (<sub [::front-config/country])
        applicant-opts  (<sub [::bond-subs/edit-applicant-select-opts entity-id])
        template-opts   (<sub [::bond-subs/edit-template-select-opts-full entity-id])]
    [(or wrapper :<>)
     [:div.flex.flex-col
      [bond-edit-form-fields
       {:tr              tr
        :bond-id         bond-id
        :edit            edit
        :show-errors?    show-errors?
        :validator       validator
        :required-fields required-fields
        :country         country
        :applicant-opts  applicant-opts
        :template-opts   template-opts
        :pending-invite? pending-invite?
        :on-field-change #(>evt [::bond-subs/set-edit-field %1 %2])}]
      [:div.flex.gap-4.mt-6
       [ui/button
        {:kind     :secondary
         :on-click #(>evt [::bond-subs/close-edit])}
        (tr [:cancel])]
       [ui/button
        {:kind     :primary
         :disabled (or saving? (not dirty?))
         :on-click #(>evt [::bond-subs/submit-edit bond required-fields])}
        (tr [:bond/save])]]]]))

(defn partial-edit-modal
  "Modal for editing verified bonds (amount, expiration, reason only)."
  [bond tr {:keys [can-extend? can-change-amount?]}]
  (let [entity-id    (:db/id bond)
        amount       (<sub [::bond-subs/edit-amount])
        expires-on   (<sub [::bond-subs/edit-expires-on])
        reason       (<sub [::bond-subs/edit-reason])
        saving?      (<sub [::bond-subs/edit-saving?])
        show-errors? (<sub [::bond-subs/edit-show-errors?])
        validator    (<sub [::bond-subs/edit-validator entity-id])]
    [modal/composed-input-modal
     {:open       true
      :on-close   #(>evt [::bond-subs/close-edit])
      :aria-label (tr [:bond/edit])
      :class-name "modal-overflow-visible"}
     (cc/modal-header {:close-modal #(>evt [::bond-subs/close-edit])
                       :title       (tr [:bond/edit])})
     (cc/modal-body
       {}
       [:div.flex.flex-col.gap-4.pb-4
        [mi/money-input
         {:id            (str "input-amount-" entity-id)
          :label-text    (tr [:bond/amount])
          :placeholder   (tr [:bond/money-whole-units])
          :default-value amount
          :required      true
          :disabled      (not can-change-amount?)
          :invalid       (validate/field-invalid? show-errors? validator :bond/amount)
          :invalid-text  (validate/field-invalid-text show-errors? validator :bond/amount tr)}
         #(>evt [::bond-subs/set-amount %])]
        [ui/date-picker
         {:id           "bond-expiration-date-picker"
          :label-text   (tr [:bond/expires-on])
          :tr           tr
          :required?    true
          :fixed?       true
          :value        expires-on
          :allow-date?  #(date-util/after? % (js/Date.))
          :callback     (fn [d]
                          (when-not (= d expires-on)
                            (>evt [::bond-subs/set-expires-on d])))
          :disabled?    (not can-extend?)
          :invalid?     (validate/field-invalid? show-errors? validator :bond/expires-on)
          :invalid-text (validate/field-invalid-text show-errors? validator :bond/expires-on tr)}]
        [ui-rt/editor-legacy
         {:id            "bond-reason"
          :label-text    (tr [:bond/reason])
          :initial-value (or reason "")
          :required?     true
          :on-change     #(>evt [::bond-subs/set-reason %])
          :placeholder   (tr [:bond/reason-placeholder])}]
        [ui/button
         {:kind     :primary
          :disabled (or saving?
                        (not (bond-util/partial-edit-dirty? bond amount expires-on))
                        (rich-text/empty-html-rich-text? reason))
          :on-click #(>evt [::bond-subs/submit-edit bond])}
         (tr [:bond/save])]])]))

(defn close-bond-modal [close-reason close-save-disabled? bond-entity-id tr]
  [modal/composed-input-modal
   {:open       true
    :on-close   #(>evt [::bond-subs/cancel-close])
    :aria-label (tr [:bond/close-bond])}
   (cc/modal-header {:close-modal #(>evt [::bond-subs/cancel-close])
                     :title       (tr [:bond/close-bond])})
   (cc/modal-body
     {}
     [:div.flex.flex-col.gap-4
      [:p (tr [:bond/confirm-close])]
      [ui-rt/editor-legacy {:id            "bond-closing-reason"
                            :label-text    (tr [:bond/closing-reason])
                            :initial-value (or close-reason "")
                            :required?     true
                            :on-change     #(>evt [::bond-subs/set-close-reason %])
                            :placeholder   (tr [:bond/closing-reason-placeholder])}]
      [:div.flex
       [ui/button
        {:kind     :primary
         :disabled close-save-disabled?
         :on-click #(>evt [::bond-subs/submit-close bond-entity-id close-reason])}
        (tr [:bond/close-bond])]]])])

(defn close-reopen-bond-buttons [tr can-close? can-reopen? bond-entity-id]
  [:<>
   (when can-close?
     [ui/button
      {:kind     :secondary
       :on-click #(>evt [::bond-subs/open-close bond-entity-id])}
      (tr [:bond/close-bond])])
   (when can-reopen?
     [ui/modal-confirmation-button
      {:trigger-button-text (tr [:bond/reopen-bond])
       :trigger-button-opts {:kind :tertiary}
       :tr                  tr
       :id                  "reopen-bond"
       :modal-title         (tr [:bond/confirm-reopen])
       :on-yes-click        #(>evt [::bond-subs/reopen-bond bond-entity-id])}
      [:p (tr [:bond/confirm-reopen])]])])

(defn bond-actions-section
  [{:keys [tr bond show-edit-button? can-full-edit? group-id can-cancel-invite? can-verify?
           req-attachments? can-close? can-reopen? entity-id]}]
  (let [has-actions? (or show-edit-button?
                         can-cancel-invite?
                         can-verify?
                         can-close?
                         can-reopen?)]
    (when has-actions?
      [:div.mt-4
       [separator/horizontal-line]
       [:div.flex.flex-wrap.justify-end.gap-4
        (when show-edit-button?
          [ui/button
           {:kind     :secondary
            :on-click (fn []
                        (>evt [::bond-subs/open-edit bond])
                        (when can-full-edit?
                          (>evt [::bond-subs/load-templates group-id])))}
           (tr [:bond/edit])])
        (when can-cancel-invite?
          [ui/modal-confirmation-button
           {:trigger-button-text (tr [:bond/cancel-invite])
            :trigger-button-opts {:kind :tertiary}
            :tr                  tr
            :id                  "cancel-bond-invite"
            :modal-title         (tr [:bond/cancel-invite])
            :on-yes-click        #(>evt [::bond-subs/cancel-bond-invitation entity-id])}
           [:p (tr [:bond/confirm-cancel-invite])]])
        (when can-verify?
          [ui/button
           {:kind     :primary
            :disabled (not req-attachments?)
            :on-click #(>evt [::bond-subs/open-verify bond])}
           (tr [:bond/verify-bond])])
        [close-reopen-bond-buttons tr can-close? can-reopen? entity-id]]])))

(defn verify-bond-modal [{:keys [tr bond close-modal-event verify-bond verifying?]}]
  (r/with-let [*external-id  (r/atom (or (:bond/external-id bond) ""))
               *audit-reason (r/atom "")]
    [modal/composed-input-modal
     {:open       true
      :on-close   #(>evt close-modal-event)
      :aria-label (tr [:bond/verify-bond])}
     (cc/modal-header {:close-modal #(>evt close-modal-event)
                       :title       (tr [:bond/verify-bond])})
     (cc/modal-body
       {}
       [:div.space-y-6
        [ui/text-input
         {:id         "bond-external-id"
          :value      @*external-id
          :required   true
          :on-change  #(reset! *external-id (futil/tval %))
          :label-text (tr [:bond/account-number])}]
        [ui-rt/editor-legacy
         {:id            "bond-verification-audit-reason"
          :initial-value (or @*audit-reason "")
          :required?     false
          :on-change     #(reset! *audit-reason %)
          :label-text    (tr [:bond/verify-modal-note])}]
        [action-buttons/save-cancel-buttons
         (util/mmap {:on-cancel      #(>evt close-modal-event)
                     :cancel-tr      :bond/modal-cancel
                     :on-save        #(verify-bond (cond-> {:bond-id     (:db/id bond)
                                                            :external-id @*external-id
                                                            :version     (:entity/version bond)}
                                                     (not (rich-text/empty-html-rich-text? @*audit-reason))
                                                     (assoc :audit-reason @*audit-reason)))
                     :save-tr        :bond/modal-confirm
                     :save-disabled? (str/blank? @*external-id)
                     :loading?       verifying?
                     :size           :field}
                    tr)]])]))

(rf/reg-event-db ::set-current-bond
  (fn [db [_ bond-id]]
    (assoc-in db [::bond-view/state :bond-id] bond-id)))

(defn bond-view [bond tr]
  (let [bond-id                                  (:bond/id bond)
        entity-id                                (:db/id bond)
        lang                                     (<sub [:language])
        group                                    (:bond/group bond)
        currency                                 (:group/currency group)
        group-tz                                 (:group/tz group)
        group-id                                 (:db/id group)
        editing-bond-id                          (<sub [::bond-subs/editing-bond-id])
        edit-open?                               (and (<sub [::bond-subs/edit-open?])
                                                      (= editing-bond-id bond-id))
        full-edit?                               (<sub [::bond-subs/edit-full?])
        in-full-edit-mode?                       (and edit-open? full-edit?)
        {:keys [owner? can-edit? can-full-edit?
                can-extend? can-change-amount?]} (<sub [::bond-subs/edit-permissions entity-id])
        current-tab                              (or (<sub [::bond-tab]) :bond/active-workspaces)
        can-close?                               (<sub [::bond-subs/can-close-bond? entity-id])
        can-cancel-invite?                       (<sub [::bond-subs/can-cancel-invite? entity-id])
        can-reopen?                              (<sub [::bond-subs/can-reopen-bond? entity-id])
        can-verify?                              (<sub [::bond-subs/can-verify-bond? entity-id])
        req-attachments?                         (<sub [::bond-has-all-required-attachments? entity-id])
        verify-open?                             (<sub [::bond-subs/verify-open? entity-id])
        close-open?                              (<sub [::bond-subs/close-open? entity-id])
        close-reason                             (<sub [::bond-subs/close-reason])
        close-save-disabled?                     (<sub [::bond-subs/close-save-disabled?])
        show-edit-button?                        (and (not in-full-edit-mode?)
                                                      (not (bond-model/cancelled-invite? bond))
                                                      can-edit?
                                                      (or can-full-edit?
                                                          (not owner?)
                                                          (and owner? (or can-extend? can-change-amount?))))]
    [:section.flex.flex-col.w-full.overflow-visible
     [:div.float-left.mb-6
      [:div.flex.justify-between.items-center
       [:h3.font-bold.mb-3.text-ui-night-sky-100
        [:p (bond-model/bond-identifier bond)]]
       [:div.flex.gap-4.items-center
        [:p (str (tr [:bond/status]) ": " (or (tr [(:domain/state bond)]) "-"))]]]
      (if in-full-edit-mode?
        [:div.overflow-visible [full-edit-form bond tr]]
        [:<>
         (bond-details bond tr)
         [separator/horizontal-line]
         (:bond/description bond)
         [separator/horizontal-line]
         [:div.flex.flex-row.flex-wrap.-mx-2.mt-2.gap-y-3
          [:div.w-full.sm:w-1-2.lg:w-1-4.px-2.my-2.mb-6
           [:<>
            [:div.bx--label (tr [:bond/owner-details])]
            [:div.label-value (bond-util/format-bond-owner lang (:bond/owner bond) tr)]]]]
         [ui-cs/content-switcher
          {:current   current-tab
           :tr        tr
           :size      :large
           :val->str  #(tr [%])
           :on-change #(>evt [::bond-tab-changed % entity-id])
           :items     [:bond/active-workspaces
                       :bond/past-workspaces
                       :bond/attachments
                       :bond/audit-log]}]
         (case current-tab
           :bond/active-workspaces [:div.mt-6 (active-workspaces-view bond tr)]
           :bond/past-workspaces [:div.mt-6 (past-workspaces-view bond tr)]
           :bond/attachments [:<>
                              [:div.mt-4
                               [bond-attachment-requirements-list tr entity-id req-attachments?]]
                              [attachment-list/list-view
                               {:target         bond
                                :can-edit-bond? (<sub [::bond-subs/can-edit-bond-attachments? entity-id])}]]
           :bond/audit-log [bond-audit-log entity-id tr currency group-tz])
         [bond-actions-section
          {:tr                 tr
           :bond               bond
           :show-edit-button?  show-edit-button?
           :can-full-edit?     can-full-edit?
           :group-id           group-id
           :can-cancel-invite? (and (not in-full-edit-mode?) can-cancel-invite?)
           :can-verify?        (and (not in-full-edit-mode?) can-verify?)
           :req-attachments?   req-attachments?
           :can-close?         can-close?
           :can-reopen?        can-reopen?
           :entity-id          entity-id}]])]
     (when close-open?
       [close-bond-modal close-reason close-save-disabled? entity-id tr])
     (when (and edit-open? (not full-edit?))
       [partial-edit-modal bond tr {:can-extend?        can-extend?
                                    :can-change-amount? can-change-amount?}])
     (when verify-open?
       [verify-bond-modal
        {:tr                tr
         :bond              bond
         :close-modal-event [::bond-subs/close-verify]
         :verify-bond       #(>evt [::bond-subs/verify-bond %])
         :verifying?        (<sub [:comm/command-processing? :bond/verify-bond])}])]))
